/**
 * @file Shared vanilla DOM element factories.
 * @module ui/dom
 * @license AGPL-3.0-only
 *
 * Zero dependencies — no p5, no framework.
 * Each function creates and returns a raw HTMLElement.
 * CSS class names use prefix 'p5t-' for easy user overrides.
 */


const CLS = 'p5t';

function _el(tag, cls, attrs) {
  const e = document.createElement(tag);
  if (cls) e.className = `${CLS}-${cls}`;
  if (attrs) for (const k in attrs) e.setAttribute(k, attrs[k]);
  return e;
}

/**
 * Flex-column container. Absolute-positioned by default (a panel floats over the
 * canvas); pass `inline` to flow it in normal document order instead, for
 * mounting it as a section inside an existing layout.
 */
function createContainer(cls, inline) {
  const c = _el('div', cls || 'ui');
  const pos = inline ? 'static' : 'absolute';
  c.style.cssText = `position:${pos};display:flex;flex-direction:column;gap:0px;`;
  return c;
}

/** Range slider. onChange receives parsed float. */
function createSlider(min, max, value, step, onChange) {
  const s = _el('input', 'slider', { type: 'range', min, max, step });
  s.value = value;
  if (onChange) s.addEventListener('input', () => onChange(parseFloat(s.value)));
  return s;
}

/** Push button. */
function createButton(label, onClick) {
  const b = _el('button', 'btn');
  b.textContent = label;
  if (onClick) b.addEventListener('click', onClick);
  return b;
}

/**
 * Checkbox with label text.
 * Returns the <label> element. Access the <input> via `.firstChild`.
 */
function createCheckbox(label, checked, onChange) {
  const lab = _el('label', 'check');
  const inp = _el('input', null, { type: 'checkbox' });
  inp.checked = !!checked;
  lab.appendChild(inp);
  lab.appendChild(document.createTextNode(' ' + label));
  if (onChange) inp.addEventListener('change', () => onChange(inp.checked));
  return lab;
}

/** Dropdown select. Options: array of values or { label, value } objects. */
function createSelect(options, value, onChange) {
  const s = _el('select', 'select');
  (options || []).forEach(o => {
    const opt = document.createElement('option');
    if (o && typeof o === 'object') {
      opt.value = o.value;
      opt.textContent = o.label ?? `${o.value}`;
    } else {
      opt.value = o;
      opt.textContent = `${o}`;
    }
    s.appendChild(opt);
  });
  if (value != null) s.value = value;
  if (onChange) s.addEventListener('change', () => onChange(s.value));
  return s;
}

/** Color picker. onChange receives hex string. */
function createColorPicker(value, onChange) {
  const c = _el('input', 'color', { type: 'color' });
  c.value = value || '#ffffff';
  return c;
}

/** Text label (span). */
function createLabel(text) {
  const l = _el('span', 'label');
  l.textContent = text;
  return l;
}

// ── Color helpers (no p5 dependency) ────────────────────────────────────────

/** '#rrggbb' -> [r, g, b, a] normalised 0-1 */
function hexToVec4(hex) {
  const h = (hex || '#000000').replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16) / 255,
    parseInt(h.substring(2, 4), 16) / 255,
    parseInt(h.substring(4, 6), 16) / 255,
    1.0
  ];
}

/** [r, g, b (, a)] normalised 0-1 -> '#rrggbb' */
function vec4ToHex(v) {
  const c = n => Math.round(Math.max(0, Math.min(1, n ?? 0)) * 255)
                    .toString(16).padStart(2, '0');
  return `#${c(v[0])}${c(v[1])}${c(v[2])}`;
}

// ── Visibility helpers (Safari-safe) ────────────────────────────────────────

/**
 * Show/hide a DOM element robustly.
 * Uses a data attribute to remember the previous display value so that
 * Safari input[range] workarounds are preserved on re-show.
 * @param {HTMLElement} el
 * @param {boolean} show
 */
function setVisible(el, show) {
  if (!el || !el.style) return;
  if (show) {
    const prev = el.dataset?._uiDisplay;
    el.style.display = prev != null ? prev : '';
    if (el.dataset) delete el.dataset._uiDisplay;
  } else {
    if (el.dataset) el.dataset._uiDisplay ??= el.style.display || '';
    el.style.display = 'none';
  }
}

/**
 * Append `child` to `parent`, ensuring parent has non-static positioning
 * so absolute children are anchored correctly.
 * Falls back to `document.body` when parent is null/undefined.
 * @param {HTMLElement} child
 * @param {HTMLElement|null} [parent]
 */
function mount(child, parent) {
  const p = parent || document.body;
  if (p.style && getComputedStyle(p).position === 'static') {
    p.style.position = 'relative';
  }
  p.appendChild(child);
}

/**
 * @file Schema-driven parameter panel — binds named controls to a target.
 * @module ui/bindUI
 * @license AGPL-3.0-only
 *
 * Zero p5 dependencies.  Pure vanilla DOM.
 *
 * ---------------------------------------------------------------------------
 * Core concept — binding
 * ---------------------------------------------------------------------------
 * A schema maps parameter names to control definitions.  Each entry produces
 * a DOM control whose value is *bound* to that name in two directions:
 *
 *   user interaction  →  control value changes  →  dirty flag set
 *   tick()            →  dirty controls pushed  →  target(name, value)
 *
 * The target is any receiver that accepts (name, value) pairs:
 *   target(name, value)          plain function
 *   target.set(name, value)      object with set method
 *
 * When target is omitted, bindings are read-only: values are accessible via
 * ui[name].value() but never pushed anywhere automatically.
 *
 * The term "binding" is intentional — this is the same concept used in shader
 * systems (uniform bindings), game-engine inspectors (property bindings), and
 * data-binding frameworks.  The schema key is the binding name; the control is
 * the binding's UI representation; the target is the binding's sink.
 *
 * ---------------------------------------------------------------------------
 * Tick model
 * ---------------------------------------------------------------------------
 * Designed for rendering pipelines where a host clock calls tick() once per
 * frame — the same clock that drives PoseTrack playback.
 *
 * Values are pushed to the target at most once per control per frame and only
 * when the control has changed since the last push (_dirty flag).  The first
 * tick always pushes all values to initialise target state.
 *
 * Multiple user interactions within a single frame (rapid slider drag,
 * programmatic set() calls) collapse to one push at tick() time.  This is the
 * correct behaviour for rendering sinks (shaders, scene params).  It is not
 * suitable for sinks that require every intermediate delta.
 *
 * ---------------------------------------------------------------------------
 * Supported control types (explicit or inferred from schema value)
 * ---------------------------------------------------------------------------
 * 'float'  : slider          'int'    : slider (integer step)
 * 'bool'   : checkbox        'color'  : color picker (→ normalised RGBA vec4)
 * 'vec2'   : 2 sliders       'vec3'   : 3 sliders      'vec4' : 4 sliders
 * 'select' : dropdown        'button' : action button (no value push)
 *
 * Type inference (when cfg.type is omitted):
 *   cfg.options        → 'select'
 *   cfg.onClick fn     → 'button'
 *   boolean value      → 'bool'
 *   array [2..4]       → 'vec2'/'vec3'/'vec4'
 *   string value       → 'color'
 *   number / default   → 'float'
 *
 * ---------------------------------------------------------------------------
 * Returned API
 * ---------------------------------------------------------------------------
 *   ui.el                       HTMLElement (container)
 *   ui.visible        get/set   boolean — whole panel visibility
 *   ui.collapsed      get/set   boolean — body visibility (requires collapsible+title)
 *   ui[name].visible  get/set   boolean — per-binding visibility
 *   ui[name].value()            current bound value
 *   ui[name].set(v)             set programmatically (marks dirty → pushed on next tick)
 *   ui[name].reset()            restore initial value (marks dirty)
 *   ui.each(fn)                 iterate bindings in schema order
 *   ui.elts()                   flat array of all bound DOM elements
 *   ui.reset()                  reset all bindings
 *   ui.parent(el)               re-mount container into a new parent HTMLElement
 *   ui.tick()                   push dirty bindings to target — call once per frame
 *   ui.dispose()                remove DOM, detach listeners
 */


// ── Helpers ──────────────────────────────────────────────────────────────────

const isBool  = v => typeof v === 'boolean';
const isArr   = Array.isArray;
const isVec   = v => isArr(v) && v.length >= 2 && v.length <= 4;
const isStr   = v => typeof v === 'string';
const toFloat = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

function inferType(cfg) {
  if (cfg.type) return cfg.type;
  if (cfg.options) return 'select';
  if (typeof cfg.onClick === 'function') return 'button';
  const v = cfg.value;
  if (isBool(v)) return 'bool';
  if (isVec(v)) return ['', 'vec2', 'vec3', 'vec4'][v.length] || 'vec4';
  if (isStr(v)) return 'color';
  return 'float';
}

// ── Factory ──────────────────────────────────────────────────────────────────

/**
 * Build a parameter panel with named bindings.
 *
 * @param {Object<string,Object>} schema  Binding definitions keyed by name.
 * @param {Object} [opt]   Layout options.
 * @param {Function|Object} [opt.target]
 *   If a function `(name, value) => ...`, called for every binding on tick.
 *   If an object with `.set(name, value)`, that method is called instead.
 *   If omitted, read values manually via `ui[name].value()`.
 * @param {number}  [opt.x=0]            Container left (px).
 * @param {number}  [opt.y=0]            Container top (px).
 * @param {number}  [opt.width=120]      Default slider/select width (px).
 * @param {number}  [opt.offset=6]       Vertical gap between rows (px).
 * @param {string}  [opt.color]          Container text color.
 * @param {boolean} [opt.hidden=false]   Start hidden.
 * @param {boolean} [opt.labels=false]   Show per-binding labels.
 * @param {string}  [opt.title]          Bold title row.
 * @param {boolean} [opt.collapsible]    Make title row a collapse toggle (requires title).
 * @param {boolean} [opt.collapsed]      Start collapsed (requires collapsible + title).
 * @param {HTMLElement} [opt.parent]     Mount target (defaults to document.body).
 * @returns {Object} Panel handle — see "Returned API" above.
 */
function createUI(schema, opt) {
  schema = schema || {};
  opt    = opt    || {};
  const _target = opt.target || null;

  const _order      = Object.keys(schema);
  const _defaults   = {};
  const _labels     = {};
  const _w          = opt.width  ?? 120;
  const _off        = opt.offset ?? 6;
  const _showLabels = !!opt.labels;

  const ui        = {};
  const container = createContainer('param-ui');
  container.style.left = `${opt.x ?? 0}px`;
  container.style.top  = `${opt.y ?? 0}px`;
  if (opt.color) container.style.color = opt.color;

  let _vis = true;

  // ── Collapsible setup ─────────────────────────────────────────────────────
  // body is the content wrapper — everything below the title goes here.
  // When not collapsible it's transparent: same layout, no extra click target.

  const canCollapse = !!(opt.title && (opt.collapsible || 'collapsed' in opt));
  let _collapsed    = canCollapse && !!opt.collapsed;

  const body = document.createElement('div');
  body.className = 'p5t-body';
  body.style.cssText = 'display:flex;flex-direction:column;gap:0px;';

  // ── Title ─────────────────────────────────────────────────────────────────

  let chevron = null;
  if (opt.title) {
    const titleRow = document.createElement('div');
    titleRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;';
    titleRow.style.marginBottom = `${_off}px`;

    const titleLabel = createLabel(opt.title);
    titleLabel.style.fontWeight = 'bold';
    titleRow.appendChild(titleLabel);

    if (canCollapse) {
      chevron = createLabel(_collapsed ? '\u25B6' : '\u25BC');
      chevron.style.cssText = 'cursor:pointer;user-select:none;margin-left:6px;';
      titleRow.style.cursor = 'pointer';
      titleRow.appendChild(chevron);
      titleRow.addEventListener('click', () => {
        _collapsed = !_collapsed;
        _applyCollapse();
      });
    }

    container.appendChild(titleRow);
  }

  // ── Per-binding builder ───────────────────────────────────────────────────

  function _setGap(el) { el.style.marginBottom = `${_off}px`; }

  function addLabel(name, cfg) {
    if (!_showLabels) return;
    const l = createLabel(cfg.label || name);
    l.style.marginBottom = `${_off}px`;
    body.appendChild(l);
    _labels[name] = l;
  }

  function wrap(name, type, el, value, set, reset) {
    const c = { type, el, value, _dirty: true, _vis: true };
    // set/reset mark dirty so the bound value is pushed on the next tick.
    c.set   = v  => { set(v);  c._dirty = true; };
    c.reset = () => { reset(); c._dirty = true; };
    Object.defineProperty(c, 'visible', {
      get() { return c._vis; },
      set(v) {
        c._vis = v !== false;
        applyControlVis(name);
      }
    });
    return c;
  }

  function applyControlVis(name) {
    const c = ui[name];
    if (!c) return;
    const show = _vis && c._vis;
    const els  = isArr(c.el) ? c.el : [c.el];
    els.forEach(e => setVisible(e, show));
    _labels[name] && setVisible(_labels[name], show);
  }

  function buildControl(name, cfg) {
    cfg = cfg || {};
    const type = inferType(cfg);
    const w    = cfg.width ?? _w;

    addLabel(name, cfg);

    // ── bool ──
    if (type === 'bool') {
      const el  = createCheckbox('', cfg.value ?? false);
      _setGap(el);
      body.appendChild(el);
      const inp = el.firstChild;
      const c = wrap(name, 'bool', el,
        () => inp.checked,
        v  => { inp.checked = !!v; },
        () => { inp.checked = !!_defaults[name]; }
      );
      inp.addEventListener('change', () => { c._dirty = true; });
      return c;
    }

    // ── button ──
    if (type === 'button') {
      const el = createButton(cfg.label || name,
        typeof cfg.onClick === 'function' ? cfg.onClick : null);
      el.style.width = `${w}px`;
      _setGap(el);
      body.appendChild(el);
      return wrap(name, 'button', el, () => null, () => {}, () => {});
    }

    // ── select ──
    if (type === 'select') {
      const el = createSelect(cfg.options, cfg.value);
      el.style.width = `${w}px`;
      _setGap(el);
      body.appendChild(el);
      const c = wrap(name, 'select', el,
        () => el.value,
        v  => { el.value = v; },
        () => { el.value = _defaults[name]; }
      );
      el.addEventListener('change', () => { c._dirty = true; });
      return c;
    }

    // ── color ──
    if (type === 'color') {
      const el = createColorPicker(cfg.value);
      el.style.width = `${w}px`;
      _setGap(el);
      body.appendChild(el);
      const c = wrap(name, 'color', el,
        () => hexToVec4(el.value),
        v  => { el.value = isStr(v) ? v : isArr(v) ? vec4ToHex(v) : v; },
        () => { el.value = _defaults[name] || '#ffffff'; }
      );
      el.addEventListener('input', () => { c._dirty = true; });
      return c;
    }

    // ── vec2 / vec3 / vec4 ──
    if (type === 'vec2' || type === 'vec3' || type === 'vec4') {
      const n    = type === 'vec2' ? 2 : type === 'vec3' ? 3 : 4;
      const vals = isArr(cfg.value) ? cfg.value : new Array(n).fill(0);
      const min  = cfg.min  ?? 0;
      const max  = cfg.max  ?? 1;
      const step = cfg.step ?? (cfg.type === 'int' ? 1 : 0.01);
      const els  = [];
      for (let i = 0; i < n; i++) {
        const s = createSlider(min, max, toFloat(vals[i] ?? 0), step);
        s.style.width = `${w}px`;
        _setGap(s);
        body.appendChild(s);
        els.push(s);
      }
      const c = wrap(name, type, els,
        ()    => els.map(s => toFloat(s.value)),
        arr   => { isArr(arr) && els.forEach((s, i) => { s.value = toFloat(arr[i] ?? 0); }); },
        ()    => { const d = _defaults[name]; isArr(d) && els.forEach((s, i) => { s.value = toFloat(d[i] ?? 0); }); }
      );
      els.forEach(s => s.addEventListener('input', () => { c._dirty = true; }));
      return c;
    }

    // ── float / int (default) ──
    const val  = toFloat(cfg.value ?? 0);
    const min  = cfg.min  ?? 0;
    const max  = cfg.max  ?? 1;
    const step = cfg.step ?? (cfg.type === 'int' ? 1 : 0.01);
    const el   = createSlider(min, max, val, step);
    el.style.width = `${w}px`;
    _setGap(el);
    body.appendChild(el);
    const c = wrap(name, cfg.type === 'int' ? 'int' : 'float', el,
      () => toFloat(el.value),
      v  => { el.value = toFloat(v); },
      () => { el.value = toFloat(_defaults[name]); }
    );
    el.addEventListener('input', () => { c._dirty = true; });
    return c;
  }

  // ── Build all bindings ─────────────────────────────────────────────────

  _order.forEach(name => {
    _defaults[name] = schema[name] ? schema[name].value : null;
    ui[name] = buildControl(name, schema[name]);
  });

  container.appendChild(body);

  // ── Collapse helper ────────────────────────────────────────────────────

  function _applyCollapse() {
    body.style.display = _collapsed ? 'none' : 'flex';
    if (chevron) chevron.textContent = _collapsed ? '\u25B6' : '\u25BC';
  }

  // ── Container visibility ───────────────────────────────────────────────

  function setContainerVis(show) {
    _vis = show !== false;
    if (_vis) {
      container.style.display       = 'flex';
      container.style.visibility    = 'visible';
      container.style.pointerEvents = 'auto';
    } else {
      container.style.display       = 'none';
      container.style.visibility    = 'hidden';
      container.style.pointerEvents = 'none';
    }
    _order.forEach(applyControlVis);
  }

  Object.defineProperty(ui, 'visible', {
    get() { return _vis; },
    set(v) { setContainerVis(v); }
  });

  Object.defineProperty(ui, 'collapsed', {
    get() { return _collapsed; },
    set(v) {
      if (!canCollapse) return;
      _collapsed = !!v;
      _applyCollapse();
    }
  });

  // ── Public API ─────────────────────────────────────────────────────────

  ui.el = container;

  ui.each = fn => {
    if (typeof fn !== 'function') return;
    _order.forEach(name => fn(name, ui[name]));
  };

  ui.elts = () => {
    const out = [];
    ui.each((_, c) => {
      if (!c || !c.el) return;
      isArr(c.el) ? c.el.forEach(e => e && out.push(e)) : out.push(c.el);
    });
    return out;
  };

  ui.reset = () => {
    _order.forEach(name => { const c = ui[name]; c && c.reset(); });
  };

  /**
   * Re-mount container into a new parent HTMLElement.
   * Accepts a raw HTMLElement (p5.Element unwrapping is the bridge's job).
   * @param {HTMLElement} el
   */
  ui.parent = el => mount(container, el);

  /**
   * Push dirty bindings to target. Call once per frame.
   *
   * Invariant: the target is called at most once per binding per frame,
   * and only if the bound value changed since the last push.
   * The first tick always pushes all bindings to initialise target state.
   */
  ui.tick = () => {
    if (!_target) return;
    const push = typeof _target === 'function'
      ? _target
      : typeof _target.set === 'function'
        ? (name, value) => _target.set(name, value)
        : null;
    if (!push) return;
    _order.forEach(name => {
      const c = ui[name];
      if (!c || c.type === 'button' || !c._dirty) return;
      c._dirty = false;
      push(name, c.value());
    });
  };

  /** Remove container from DOM. */
  ui.dispose = () => {
    container.parentNode && container.parentNode.removeChild(container);
  };

  // ── Mount & initial state ──────────────────────────────────────────────

  mount(container, opt.parent);
  setContainerVis(!opt.hidden);
  _applyCollapse();

  return ui;
}

/**
 * @file DOM-based transport controls for PoseTrack (or any compatible target).
 * @module ui/trackUI
 * @license AGPL-3.0-only
 *
 * Zero p5 dependencies.  Pure vanilla DOM.
 *
 * Transport model
 * ---------------
 *   The Play/Pause button is the **sole** control that starts or stops playback.
 *   The rate slider adjusts speed while playing but never starts or stops.
 *   rate === 0 is treated as "frozen" — playback state is unchanged.
 *   The seek slider scrubs position without affecting the playing flag.
 *   The loop checkbox and bounce checkbox change looping behaviour without
 *   starting playback.
 *
 * Loop modes
 * ----------
 *   loop:false, bounce:false  — play once, stop at end
 *   loop:true,  bounce:false  — repeat, wrap back to start
 *   loop:true,  bounce:true   — bounce forever at boundaries
 *   loop:false, bounce:true   — bounce once: flip at far boundary, stop at origin
 *
 *   bounce and loop are independent — no exclusivity enforced.
 *   Both checkboxes are always visible.
 *
 * Target contract (duck-typed)
 * ----------------------------
 *   target.play(opts?)   Start or update playback.
 *   target.stop()        Stop playback.
 *   target.seek(t)       Set normalised position [0, 1].
 *   target.time()        Returns normalised position [0, 1].
 *   target.playing       Boolean — true while playing.
 *   target._onPlay       Reserved lib-space slot — assigned by this panel.
 *   target._onEnd        Reserved lib-space slot — assigned by this panel.
 *   target._onStop       Reserved lib-space slot — assigned by this panel.
 *
 * Optional:
 *   target.add(d?)       Add keyframe at depth d [0..1] (near..far plane centre).
 *   target.reset()       Clear all keyframes and stop.
 *   target.info()        Returns { keyframes, segments, seg, f, time, ... }.
 *
 * State initialisation
 * --------------------
 *   _rate is seeded once at creation from the live track state (target.rate)
 *   with opt.rate as fallback.  After creation rate is fully UI-owned — the
 *   panel never reads rate back from the track.  This prevents spurious slider
 *   snaps when external play() calls omit rate (e.g. play({ bounce: true })).
 *
 *   _loop and _bounce are seeded the same way and additionally polled from
 *   the track every tick() while playing, so external play() calls that change
 *   loop/bounce mode are always reflected.
 *
 * Layout (top → bottom)
 * ---------------------
 *   Title row  — optional, becomes collapse toggle when collapsible=true
 *   Row 1  — controls:  [+]  [▶/⏸]  [↺]   (each independently optional)
 *   Row 1b — depth:     depth slider        (when target supports add)
 *   Row 2  — seek:      seek slider         (hidden when keyframes ≤ 1)
 *   Row 3  — rate:      rate label + slider (when showProps)
 *   Row 4  — loop + bounce: both checkboxes always visible, independent
 *   Row 5  — info:      time / keyframe     (when showInfo)
 *
 * Returned API
 * ------------
 *   ui.el              HTMLElement container
 *   ui.visible         get/set boolean
 *   ui.collapsed       get/set boolean (requires collapsible + title)
 *   ui.parent(el)      Re-mount container into a new parent HTMLElement.
 *   ui.tick()          Sync seek slider, play button, and enabled state from target.
 *   ui.dispose()       Remove DOM and clear lib-space hooks.
 */


/**
 * Build a track transport UI.
 *
 * @param {Object} target    PoseTrack (or duck-compatible object).
 * @param {Object} [opt]     Options.
 * @param {boolean} [opt.seek=true]       Show seek slider.
 * @param {boolean} [opt.props=true]      Show rate slider + loop controls.
 * @param {boolean} [opt.info=false]      Show time/keyframe readout.
 * @param {boolean} [opt.play=true]       Show play/pause button. false hides it —
 *                                        seek slider becomes the sole transport control.
 * @param {boolean} [opt.add=true]        Show the add button when the target exposes
 *                                        add(). false hides it (and the depth slider).
 * @param {boolean} [opt.reset=true]      Show the reset button when the target exposes
 *                                        reset(). false hides it.
 * @param {number}  [opt.rate=1]          Initial rate (overridden by target.rate if set).
 * @param {boolean} [opt.loop=false]      Initial loop state (overridden by target.loop).
 * @param {boolean} [opt.bounce=false]    Initial bounce state (overridden by target.bounce).
 * @param {number}  [opt.depth=0.5]       Initial add-pose depth [0..1]: 0 = near, 1 = far.
 * @param {number}  [opt.x=0]            Container left (px).
 * @param {number}  [opt.y=0]            Container top (px).
 * @param {number}  [opt.width=220]      Slider width (px).
 * @param {number}  [opt.rateWidth]      Rate slider width (px). Defaults to opt.width.
 * @param {number}  [opt.depthWidth]     Depth slider width (px). Defaults to opt.width.
 * @param {string}  [opt.color]          Text color.
 * @param {boolean} [opt.hidden=false]   Start hidden.
 * @param {string}  [opt.title]          Optional title row.
 * @param {boolean} [opt.collapsible]    Make title row a collapse toggle (requires title).
 * @param {boolean} [opt.collapsed]      Start collapsed (requires collapsible + title).
 * @param {HTMLElement} [opt.parent]     Mount target (defaults to document.body).
 * @returns {Object} UI handle.
 */
function createTrackUI(target, opt) {
  opt = opt || {};

  const showSeek  = opt.seek  !== false;
  const showProps = opt.props !== false;
  const showInfo  = opt.info  === true;
  const showPlay  = opt.play  !== false;
  const sliderW      = opt.width      ?? 120;
  const rateSliderW  = opt.rateWidth  ?? sliderW;
  const depthSliderW = opt.depthWidth ?? sliderW;

  // ── Seed _rate, _loop, _bounce from live track state, fall back to opt ──

  let _rate   = (typeof target.rate === 'number') ? target.rate : (opt.rate ?? 1);
  let _loop   = !!(target.loop   || opt.loop);
  let _bounce = !!(target.bounce || opt.bounce);
  let _depth  = (typeof opt.depth === 'number') ? opt.depth : 0.5;

  const container = createContainer('track-ui');
  container.style.left = `${opt.x ?? 0}px`;
  container.style.top  = `${opt.y ?? 0}px`;
  if (opt.color) container.style.color = opt.color;

  let _vis     = true;
  let _seeking = false;
  let _lastKf  = -1;

  /** Assemble play() options from current UI state. */
  function _playOpts() {
    return { rate: _rate, loop: _loop, bounce: _bounce };
  }

  /** Keyframe count from target.info(), or -1 if unavailable. */
  function _kfCount() {
    return (typeof target.info === 'function') ? target.info().keyframes : -1;
  }

  // ── Collapsible setup ─────────────────────────────────────────────────────

  const canCollapse = !!(opt.title && (opt.collapsible || 'collapsed' in opt));
  let _collapsed    = canCollapse && !!opt.collapsed;
  let chevron       = null;

  const body = document.createElement('div');
  body.className = 'p5t-body';
  body.style.cssText = 'display:flex;flex-direction:column;gap:0px;';

  // ── Title row (optional) ──────────────────────────────────────────────────

  if (opt.title) {
    const titleRow = document.createElement('div');
    titleRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;';

    const titleLabel = createLabel(opt.title);
    titleLabel.style.fontWeight = 'bold';
    titleRow.appendChild(titleLabel);

    if (canCollapse) {
      chevron = createLabel(_collapsed ? '\u25B6' : '\u25BC');
      chevron.style.cssText = 'cursor:pointer;user-select:none;margin-left:6px;';
      titleRow.style.cursor = 'pointer';
      titleRow.appendChild(chevron);
      titleRow.addEventListener('click', () => {
        _collapsed = !_collapsed;
        _applyCollapse();
      });
    }

    container.appendChild(titleRow);
  }

  // ── Row 1 — controls: [+] [▶/⏸] [↺] ─────────────────────────────────────
  //
  // Each button is independently optional — capability gated by opt:
  //   hasAdd   — target exposes add()   and opt.add   !== false   (+ button)
  //   showPlay — opt.play !== false                               (play/pause button)
  //   hasReset — target exposes reset() and opt.reset !== false   (reset button)
  //
  // The row is only appended when at least one button is present, so that
  // fully button-free panels produce no empty DOM row.

  const ctrlRow = document.createElement('div');
  ctrlRow.className = 'p5t-controls';
  ctrlRow.style.cssText = 'display:flex;gap:4px;margin-bottom:4px;align-items:center;';

  const hasAdd = typeof target.add === 'function' && opt.add !== false;
  if (hasAdd) {
    const btnAdd = createButton('\u002B', () => {
      target.add(_depth);
      _lastKf = -1;
    });
    btnAdd.title = 'Add keyframe';
    ctrlRow.appendChild(btnAdd);
  }

  let btnPlay = null;
  if (showPlay) {
    btnPlay = createButton('\u25B6', () => {
      if (target.playing) {
        target.stop();
      } else {
        target.play(_playOpts());
      }
      _syncPlayBtn();
    });
    btnPlay.title = 'Play / Pause';
    ctrlRow.appendChild(btnPlay);
  }

  let btnReset = null;
  const hasReset = typeof target.reset === 'function' && opt.reset !== false;
  if (hasReset) {
    btnReset = createButton('\u21BA', () => {
      target.reset();
      _syncPlayBtn();
      _lastKf = -1;
    });
    btnReset.title = 'Reset (clear keyframes)';
    ctrlRow.appendChild(btnReset);
  }

  if (hasAdd || showPlay || hasReset) {
    body.appendChild(ctrlRow);
  }

  // ── Row 1b — depth slider ─────────────────────────────────────────────────

  if (hasAdd && opt.depth !== false) {
    const depthRow = document.createElement('div');
    depthRow.className = 'p5t-depth';
    depthRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:2px;font-size:11px;';

    const depthLabel = createLabel(`depth: ${_depth.toFixed(2)}`);
    depthLabel.style.minWidth = '72px';

    const depthSlider = createSlider(0, 1, _depth, 0.01, v => {
      _depth = v;
      depthLabel.textContent = `depth: ${v.toFixed(2)}`;
    });
    depthSlider.style.width = `${depthSliderW}px`;

    depthRow.appendChild(depthLabel);
    depthRow.appendChild(depthSlider);
    body.appendChild(depthRow);
  }

  // ── Row 2 — seek slider ───────────────────────────────────────────────────

  let seekSlider, seekLabel;
  if (showSeek) {
    const seekRow = document.createElement('div');
    seekRow.className = 'p5t-seek';
    seekRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:2px;font-size:11px;';

    seekLabel = createLabel('seek: 0.000');
    seekLabel.style.minWidth = '72px';

    seekSlider = createSlider(0, 1, 0, 0.001, v => {
      _seeking = true;
      seekLabel.textContent = `seek: ${parseFloat(v).toFixed(3)}`;
      target.seek(v);
    });
    seekSlider.style.width = `${sliderW}px`;
    seekSlider.addEventListener('change',    () => { _seeking = false; });
    seekSlider.addEventListener('pointerup', () => { _seeking = false; });
    seekSlider.addEventListener('touchend',  () => { _seeking = false; });

    seekRow.appendChild(seekLabel);
    seekRow.appendChild(seekSlider);
    body.appendChild(seekRow);
  }

  // ── Row 3 — rate slider ───────────────────────────────────────────────────

  let rateSlider, rateLabel;
  if (showProps) {
    const rateRow = document.createElement('div');
    rateRow.className = 'p5t-rate';
    rateRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:2px;font-size:11px;';

    rateLabel = createLabel(`rate: ${_rate.toFixed(2)}`);
    rateLabel.style.minWidth = '72px';

    rateSlider = createSlider(-2, 2, _rate, 0.05, v => {
      _rate = v;
      rateLabel.textContent = `rate: ${v.toFixed(2)}`;
      if (target.playing) target.play({ rate: _rate });
    });
    rateSlider.style.width = `${rateSliderW}px`;

    rateRow.appendChild(rateLabel);
    rateRow.appendChild(rateSlider);
    body.appendChild(rateRow);
  }

  // ── Row 4 — loop + bounce checkboxes ─────────────────────────────────────

  let loopInp, bounceInp;

  if (showProps) {
    const loopRow = document.createElement('div');
    loopRow.className = 'p5t-loop';
    loopRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:4px;font-size:11px;';

    const loopLabel = createLabel('loop:');
    loopLabel.style.minWidth = '40px';

    const loopCheck = createCheckbox('', _loop, v => {
      _loop = v;
      if (target.playing) target.play(_playOpts());
    });
    loopInp = loopCheck.firstChild;

    const bounceLabel = createLabel('bounce:');
    bounceLabel.style.marginLeft = '10px';

    const bounceCheck = createCheckbox('', _bounce, v => {
      _bounce = v;
      if (target.playing) target.play(_playOpts());
    });
    bounceInp = bounceCheck.firstChild;

    loopRow.appendChild(loopLabel);
    loopRow.appendChild(loopCheck);
    loopRow.appendChild(bounceLabel);
    loopRow.appendChild(bounceCheck);
    body.appendChild(loopRow);
  }

  // ── Row 5 — info label (optional) ────────────────────────────────────────

  let infoLabel;
  if (showInfo) {
    infoLabel = createLabel('');
    infoLabel.style.cssText = 'font-size:11px;opacity:0.8;margin-bottom:4px;';
    body.appendChild(infoLabel);
  }

  container.appendChild(body);

  // ── Lib-space hooks ───────────────────────────────────────────────────────

  target._onPlay = () => { _syncPlayBtn(); };
  target._onEnd  = () => { _syncPlayBtn(); };
  target._onStop = () => { _syncPlayBtn(); };

  // ── Internal helpers ──────────────────────────────────────────────────────

  function _syncPlayBtn() {
    if (!btnPlay) return;
    btnPlay.textContent = target.playing ? '\u23F8' : '\u25B6';
  }

  function _updateEnabledState() {
    const kf = _kfCount();
    if (kf === _lastKf) return;
    _lastKf = kf;
    if (btnPlay)    btnPlay.disabled    = kf === 0;
    if (btnReset)   btnReset.disabled   = kf === 0;
    if (seekSlider) seekSlider.disabled = kf < 2;
  }

  function _updateInfo() {
    if (!infoLabel) return;
    if (typeof target.info !== 'function') { infoLabel.textContent = ''; return; }
    const i   = target.info();
    const pct = (i.time * 100).toFixed(1);
    infoLabel.textContent = `${pct}%  seg ${i.seg}/${i.segments}  kf ${i.keyframes}`;
  }

  function _applyCollapse() {
    body.style.display = _collapsed ? 'none' : 'flex';
    if (chevron) chevron.textContent = _collapsed ? '\u25B6' : '\u25BC';
  }

  // ── Visibility ────────────────────────────────────────────────────────────

  function _setVis(show) {
    _vis = show !== false;
    container.style.display = _vis ? 'flex' : 'none';
  }

  // ── Public API ────────────────────────────────────────────────────────────

  const ui = {};
  ui.el = container;

  Object.defineProperty(ui, 'visible', {
    get() { return _vis; },
    set(v) { _setVis(v); }
  });

  Object.defineProperty(ui, 'collapsed', {
    get() { return _collapsed; },
    set(v) {
      if (!canCollapse) return;
      _collapsed = !!v;
      _applyCollapse();
    }
  });

  ui.parent = parentEl => mount(container, parentEl);

  ui.tick = () => {
    if (!_seeking && seekSlider) {
      const t = typeof target.time === 'function' ? target.time() : 0;
      seekSlider.value = t;
      if (seekLabel) seekLabel.textContent = `seek: ${t.toFixed(3)}`;
    }
    _syncPlayBtn();
    _updateEnabledState();
    if (showInfo) _updateInfo();

    // Poll loop/bounce from track — covers external play() calls.
    if (showProps && target.playing) {
      const liveLoop   = !!target.loop;
      const liveBounce = !!target.bounce;
      if (liveLoop !== _loop) {
        _loop = liveLoop;
        if (loopInp) loopInp.checked = _loop;
      }
      if (liveBounce !== _bounce) {
        _bounce = liveBounce;
        if (bounceInp) bounceInp.checked = _bounce;
      }
    }
  };

  ui.dispose = () => {
    target._onPlay = null;
    target._onEnd  = null;
    target._onStop = null;
    container.parentNode && container.parentNode.removeChild(container);
  };

  // ── Mount & initial state ─────────────────────────────────────────────────

  mount(container, opt.parent);
  _setVis(!opt.hidden);
  _applyCollapse();
  _syncPlayBtn();
  _updateEnabledState();

  return ui;
}

/**
 * @file Helm profile panel — per-DOF profile editor + live activity meters.
 * @module ui/helmUI
 * @license AGPL-3.0-only
 *
 * Zero p5 dependencies.  Pure vanilla DOM.  Zero core import — a helm is
 * recognised structurally (it exposes feed / profile / activity), never by type.
 *
 * ---------------------------------------------------------------------------
 * What this edits
 * ---------------------------------------------------------------------------
 * A 6-DOF helm's behaviour is shaped by one flat `profile` object — six channels
 * (Tx Ty Tz Rp Ry Rr), each { sign, sens, lane } — plus a global `deadzone`.
 * This builder is the config surface for exactly that data: the helm sibling of
 * the track transport panel.  Where the transport panel drives a timeline
 * cursor, this edits shaping data.  It writes the live `helm.profile` /
 * `helm.deadzone` BY REFERENCE — immediate, no copy step, no tick latency on the
 * edit side.
 *
 * Per DOF, one row:
 *   - a SIGNED slider spanning [-max, +max] with 0 at centre.  Distance from
 *     centre is `sens`, the side is `sign`; dragging through 0 flips the sign —
 *     one control, no separate sign toggle.  At 0 the DOF is muted (sens 0).
 *   - a `lane` cycle-button (0 -> 1 -> 2): the input channel feeding this DOF,
 *     the manual counterpart to a device calibrate sweep.  DISABLED while the
 *     slider sits at 0 — a muted DOF routes no channel and shows no activity, so
 *     there is nothing to permute.
 *   - an ACTIVITY meter: a thin bipolar read-out (the one non-control element,
 *     since a meter has no native-slider equivalent) that tick() drives from
 *     `helm.activity(out6)`, showing push vs pull.
 *
 * Plus a global `deadzone` slider, and — only with { frame: true } — an
 * EYE | WORLD | SELF selector writing `helm.from` (the named frames a pose helm
 * accepts; a mat4 is a live matrix, not a panel choice, and stays code-set).
 *
 * When a helm carries an input `filter` (the 1€ `oneEuro`), two more sliders —
 * `minCutoff` (Hz) and `beta` (unitless) — appear beside the deadzone, built
 * once and revealed by tick() on the null->attached transition, then seeded from
 * the live filter and panel-owned thereafter.  Hidden (no filter) by default.
 *
 * ---------------------------------------------------------------------------
 * Push and pull (like the transport panel)
 * ---------------------------------------------------------------------------
 * Editing writes the profile immediately (push).  Each tick() reads
 * `helm.activity(out6)` back to drive the meters and re-syncs the lane buttons
 * from the live profile (pull), so a sketch-side calibrate that rewrites a lane
 * shows up here.  The same shape as the transport panel pushing rate / loop
 * while pulling the seek cursor.
 *
 * ---------------------------------------------------------------------------
 * Returned API (mirrors the track / param builders)
 * ---------------------------------------------------------------------------
 *   ui.el              HTMLElement container
 *   ui.visible  get/set boolean
 *   ui.parent(el)      re-mount container into a new parent HTMLElement
 *   ui.tick()          drive meters + lane sync — call once per frame
 *   ui.dispose()       remove container from DOM
 */


// Channel order — matches the helm's activity(out6) layout.  Local, not
// imported: the panel knows the profile shape by contract, never by type.
const CHANNELS = ['Tx', 'Ty', 'Tz', 'Rp', 'Ry', 'Rr'];

// Per-channel signed-slider bounds.  Translation channels carry order-1 sens;
// rotation channels are ~two orders finer (radians-per-unit).  These bound the
// slider [-max, +max] and set its step; they are presentation bounds, not helm
// limits (the profile accepts any number).
const T_MAX = 1,    T_STEP = 0.01;
const R_MAX = 0.01, R_STEP = 0.0002;

// Conditioning-slider presentation bounds — the 1€ filter knobs, shown only
// when a filter is attached.  minCutoff in Hz (the rest cutoff); beta unitless
// (how fast the cutoff opens with speed).  Presentation bounds, not filter
// limits — the filter accepts any number, as the profile does.
const MINCUT_MIN = 0.1, MINCUT_MAX = 10, MINCUT_STEP = 0.1;
const BETA_MIN   = 0,   BETA_MAX   = 2,  BETA_STEP   = 0.01;

// The named frames a pose helm accepts (the helm.from contract; a mat4 is
// code-only).  String values match the helm's own constants.
const FRAMES = [
  { value: 'EYE',   label: 'EYE (screen)' },
  { value: 'WORLD', label: 'WORLD (fixed)' },
  { value: 'SELF',  label: 'SELF (body)' },
];

// Translation channels start with 'T'.
const _isT = (ch) => ch.charCodeAt(0) === 84;

/**
 * Build a helm profile panel.
 *
 * @param {Object} helm  A PoseHelm (recognised by feed / profile / activity).
 * @param {Object} [opt]
 * @param {boolean} [opt.frame=false]  Show the EYE|WORLD|SELF frame selector.
 * @param {number}  [opt.x=0]          Container left (px).
 * @param {number}  [opt.y=0]          Container top (px).
 * @param {number}  [opt.width=130]    Signed-slider width (px).
 * @param {string}  [opt.color]        Container text colour (meters inherit it).
 * @param {boolean} [opt.inline=false] Flow inline instead of the absolute float.
 * @param {Function}[opt.onChange]     Called after any user edit of the profile.
 * @param {boolean} [opt.hidden=false] Start hidden.
 * @param {string}  [opt.title]        Optional bold title row.
 * @param {HTMLElement} [opt.parent]   Mount target (defaults to document.body).
 * @returns {Object} Panel handle — see "Returned API" above.
 */
function createHelmUI(helm, opt) {
  opt = opt || {};
  const profile = helm.profile;
  const sliderW = opt.width ?? 130;

  const container = createContainer('helm-ui', opt.inline);
  if (!opt.inline) {
    container.style.left = `${opt.x ?? 0}px`;
    container.style.top  = `${opt.y ?? 0}px`;
  }
  if (opt.color) container.style.color = opt.color;

  let _vis = true;

  // Fire the edit callback (if any) after a user change. Calibration writes the
  // profile directly, not through here, so a sketch calls onChange itself there.
  const _changed = () => { if (opt.onChange) opt.onChange(); };

  // ── Optional title ──────────────────────────────────────────────────────────

  if (opt.title) {
    const t = createLabel(opt.title);
    t.style.fontWeight = 'bold';
    t.style.marginBottom = '6px';
    container.appendChild(t);
  }

  // ── Per-DOF rows ────────────────────────────────────────────────────────────
  // Each controller is read every tick: { ch, laneBtn, fill }.  The slider is
  // write-only (user → profile); the profile is the source of truth elsewhere.

  const rows = [];
  const _act = [0, 0, 0, 0, 0, 0];   // helm.activity(out6) scratch — reused each tick

  for (const ch of CHANNELS) {
    const c    = profile[ch];
    const max  = _isT(ch) ? T_MAX  : R_MAX;
    const step = _isT(ch) ? T_STEP : R_STEP;

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:3px;';

    const name = createLabel(ch);
    name.style.minWidth = '24px';

    // lane cycle-button — disabled while the DOF is muted (sens 0).
    const laneBtn = createButton(`${c.lane}`, () => {
      c.lane = (c.lane + 1) % 3;
      laneBtn.textContent = `${c.lane}`;
      _changed();
    });
    laneBtn.title = 'input lane (click to cycle)';
    laneBtn.style.cssText = 'min-width:24px;padding:2px 6px;';
    laneBtn.disabled = c.sens === 0;

    // signed slider — value is sign*sens; drag through 0 flips sign / mutes.
    const slider = createSlider(-max, max, c.sign * c.sens, step, (v) => {
      c.sens = Math.abs(v);
      if (v > 0) c.sign = 1;
      else if (v < 0) c.sign = -1;     // at exactly 0 keep the last sign — the DOF is muted anyway
      laneBtn.disabled = c.sens === 0;
      _changed();
    });
    slider.style.width = `${sliderW}px`;

    // activity meter — bipolar fill from centre, driven each tick.  currentColor
    // ties the fill to opt.color (or the inherited text colour).
    const meter = document.createElement('div');
    meter.className = 'p5t-helm-meter';
    meter.style.cssText =
      'position:relative;flex:1;min-width:34px;height:6px;border-radius:3px;' +
      'background:rgba(127,127,127,0.18);overflow:hidden;';
    const fill = document.createElement('i');
    fill.style.cssText =
      'position:absolute;top:0;bottom:0;left:50%;width:0;background:currentColor;opacity:0.35;';
    meter.appendChild(fill);

    row.appendChild(name);
    row.appendChild(laneBtn);
    row.appendChild(slider);
    row.appendChild(meter);
    container.appendChild(row);

    rows.push({ ch, laneBtn, fill });
  }

  // ── Global deadzone ─────────────────────────────────────────────────────────

  const dzRow = document.createElement('div');
  dzRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin:6px 0 3px;';
  const dzLabel = createLabel('deadzone');
  dzLabel.style.minWidth = '54px';
  const dzSlider = createSlider(0, 60, helm.deadzone, 1, (v) => { helm.deadzone = v; _changed(); });
  dzSlider.style.flex = '1';
  dzRow.appendChild(dzLabel);
  dzRow.appendChild(dzSlider);
  container.appendChild(dzRow);

  // ── Value-layer conditioning sliders (shown only with an attached filter) ──
  // Built once as deadzone siblings; hidden until helm.filter is non-null.  On
  // the null->attached edge tick() reveals them and seeds from the live filter;
  // panel-owned thereafter.  Writes are guarded on the filter still existing.

  const mcRow = document.createElement('div');
  mcRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin:3px 0;';
  const mcLabel = createLabel('minCutoff');
  mcLabel.style.minWidth = '54px';
  const mcSlider = createSlider(MINCUT_MIN, MINCUT_MAX, 1, MINCUT_STEP, (v) => {
    if (helm.filter) { helm.filter.minCutoff = v; _changed(); }
  });
  mcSlider.style.flex = '1';
  mcRow.appendChild(mcLabel);
  mcRow.appendChild(mcSlider);
  container.appendChild(mcRow);

  const betaRow = document.createElement('div');
  betaRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin:3px 0;';
  const betaLabel = createLabel('beta');
  betaLabel.style.minWidth = '54px';
  const betaSlider = createSlider(BETA_MIN, BETA_MAX, 0, BETA_STEP, (v) => {
    if (helm.filter) { helm.filter.beta = v; _changed(); }
  });
  betaSlider.style.flex = '1';
  betaRow.appendChild(betaLabel);
  betaRow.appendChild(betaSlider);
  container.appendChild(betaRow);

  // Hidden until a filter attaches; tick() reconciles against helm.filter.
  setVisible(mcRow, false);
  setVisible(betaRow, false);
  let _filterShown = false;

  // ── Optional frame selector (pose-helm frame; named values only) ────────────

  if (opt.frame) {
    const frRow = document.createElement('div');
    frRow.style.cssText = 'display:flex;align-items:center;gap:6px;margin:3px 0;';
    const frLabel = createLabel('frame');
    frLabel.style.minWidth = '54px';
    const init  = (typeof helm.from === 'string') ? helm.from : 'EYE';
    const frSel = createSelect(FRAMES, init, (v) => { helm.from = v; _changed(); });
    frSel.style.flex = '1';
    frRow.appendChild(frLabel);
    frRow.appendChild(frSel);
    container.appendChild(frRow);
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  const ui = { el: container };

  Object.defineProperty(ui, 'visible', {
    get() { return _vis; },
    set(v) {
      _vis = v !== false;
      container.style.display = _vis ? 'flex' : 'none';
    },
  });

  ui.parent = (el) => mount(container, el);

  /**
   * Reconcile the conditioning sliders against helm.filter, then drive the
   * activity meters and re-sync the lane buttons from the live profile.  Call
   * once per frame.  The fill fraction is normalised per channel by
   * sens*helm.fullScale so translation and rotation read on one scale.
   */
  ui.tick = () => {
    // Reveal / hide the conditioning sliders against the live filter, seeding
    // them from it on the null->attached edge (panel-owned thereafter).
    const hasFilter = helm.filter != null;
    if (hasFilter !== _filterShown) {
      setVisible(mcRow, hasFilter);
      setVisible(betaRow, hasFilter);
      if (hasFilter) {
        mcSlider.value   = helm.filter.minCutoff;
        betaSlider.value = helm.filter.beta;
      }
      _filterShown = hasFilter;
    }

    helm.activity(_act);
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i], c = profile[r.ch];
      // lane button reflects the live profile (covers a sketch-side calibrate).
      const laneStr = `${c.lane}`;
      if (r.laneBtn.textContent !== laneStr) r.laneBtn.textContent = laneStr;
      r.laneBtn.disabled = c.sens === 0;
      // meter — bipolar fill normalised by helm.fullScale: activity() is post
      // sign*sens, so |activity| / (sens*fullScale) = |rawRate| / fullScale,
      // putting translation and rotation on one 0..1 scale despite differing
      // sens.  Reads the live helm field, exactly as deadzone reads helm.deadzone.
      const a   = _act[i];
      const f   = c.sens > 0 ? Math.min(Math.abs(a) / (c.sens * helm.fullScale), 1) : 0;
      const pct = f * 50;
      r.fill.style.left    = a >= 0 ? '50%' : `${50 - pct}%`;
      r.fill.style.width   = `${pct}%`;
      r.fill.style.opacity = f > 0.001 ? '1' : '0.35';
    }
  };

  ui.dispose = () => {
    container.parentNode && container.parentNode.removeChild(container);
  };

  mount(container, opt.parent);
  ui.visible = !opt.hidden;

  return ui;
}

/**
 * @file UI package entry point — parameter panels and transport controls.
 * @module ui
 * @license AGPL-3.0-only
 *
 * Pure vanilla DOM.  Zero p5 dependencies.
 * Mount into any container (canvas parent, Vue, React, plain HTML).
 *
 * Duck-type contract for dispatch (checked in order):
 *   typeof first?.play === 'function'  →  track panel  (_createTrackUI)
 *   typeof first?.feed === 'function'  →  helm panel   (_createHelmUI)
 *   otherwise                          →  param panel  (_createUI)
 */


/**
 * Unified panel factory.
 *
 * First argument determines the panel type:
 *
 *   createPanel(track, opt)   — transport controls
 *     track must expose: play, stop, seek, time, playing
 *     target.add present     → + button (suppress with opt.add: false)
 *     target.reset present   → ↺ button (suppress with opt.reset: false)
 *
 *   createPanel(helm, opt)    — helm profile controls
 *     helm must expose: feed, profile, activity
 *     opt.frame present      → EYE|WORLD|SELF selector (pose helms)
 *
 *   createPanel(schema, opt)  — parameter controls
 *     schema is a plain object of control definitions (no .play / .feed)
 *     opt.target (function|{set}) → values pushed each tick
 *
 * All paths share the same layout options: x, y, width, color, hidden, parent.
 *
 * @param {Object} trackOrSchema  A track, a helm, or a plain schema object.
 * @param {Object} [opt]
 * @returns {Object} UI handle with .el, .tick(), .dispose().
 */
function createPanel(trackOrSchema, opt) {
  if (typeof trackOrSchema?.play === 'function') {
    return createTrackUI(trackOrSchema, opt);
  }
  if (typeof trackOrSchema?.feed === 'function') {
    return createHelmUI(trackOrSchema, opt);
  }
  return createUI(trackOrSchema, opt);
}

export { createPanel };
//# sourceMappingURL=index.js.map
